import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Chip,
    Paper,
    TextField,
    useTheme,
    Avatar,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTopicLabel } from './useTopicLabel';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ReferenceLine,
} from 'recharts';
import { fetchDailyPoliticalMentions } from '../../api/podcasts';
import { getColorForCandidate, getColorForTopic, CANDIDATE_IMAGES, ELECTION_MILESTONES } from './electionColors';

// Helper to convert ISO date to YYYY-MM-DD for input
const toDateInputValue = (isoDate: string): string => {
    return isoDate.split('T')[0];
};

// Helper to find the formatted date label for a milestone in chartData
const findMilestoneLabel = (chartData: any[], targetDate: string): string | null => {
    const targetDateObj = new Date(targetDate + 'T12:00:00.000Z');

    for (const entry of chartData) {
        const entryDate = new Date(entry.fullDate + 'T12:00:00.000Z');

        if (entry.fullDate === targetDate) {
            return entry.date;
        }

        const weekEnd = new Date(entryDate);
        weekEnd.setDate(weekEnd.getDate() + 6);

        if (targetDateObj >= entryDate && targetDateObj <= weekEnd) {
            return entry.date;
        }
    }
    return null;
};

// Custom label component for reference lines
const ReferenceLineLabel = ({ viewBox, value, fill, fullLabel }: any) => {
    const theme = useTheme();
    const { x, y } = viewBox;
    const labelY = y - 18;
    const rectHeight = 20;
    const rectWidth = 70;
    const rectX = x - rectWidth / 2;
    const textX = x;

    return (
        <g>
            <title>{fullLabel}</title>
            <rect
                x={rectX}
                y={labelY - rectHeight / 2}
                width={rectWidth}
                height={rectHeight}
                rx={6}
                fill={theme.palette.background.paper}
                stroke={fill}
                strokeWidth={1.5}
                style={{ cursor: 'help', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }}
            />
            <text
                x={textX}
                y={labelY}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={fill}
                fontSize={10}
                fontWeight={600}
                style={{ cursor: 'help' }}
            >
                {value}
            </text>
        </g>
    );
};

interface MentionShareTabProps {
    selectedRange: {
        from: string;
        to: string;
        defaultView?: 'daily' | 'weekly';
    };
    includeRadio: boolean;
    isActive: boolean;
}

interface DailyMentionData {
    date: string;
    fullDate?: string;
    [candidate: string]: any;
}

export default function MentionShareTab({ selectedRange, includeRadio, isActive }: MentionShareTabProps) {
    const theme = useTheme();
    const { t } = useTranslation('elecciones');
    const topicLabel = useTopicLabel();
    const [userSelectedCandidates, setUserSelectedCandidates] = useState<string[] | null>(null);
    const [userSelectedTopics, setUserSelectedTopics] = useState<string[] | null>(null);
    const [weeklyStatus, setWeeklyStatus] = useState<'daily' | 'weekly'>(selectedRange.defaultView || 'weekly');

    // Internal date state
    const [fromDate, setFromDate] = useState<string>(toDateInputValue(selectedRange.from));
    const [toDate, setToDate] = useState<string>(toDateInputValue(selectedRange.to));

    // Update dates when selectedRange changes
    useEffect(() => {
        setFromDate(toDateInputValue(selectedRange.from));
        setToDate(toDateInputValue(selectedRange.to));
    }, [selectedRange.from, selectedRange.to]);

    useEffect(() => {
        setWeeklyStatus(selectedRange.defaultView || 'weekly');
    }, [selectedRange.defaultView]);

    // Fetch daily/weekly mention data for candidates
    const { data: dailyMentions = [], isLoading, error } = useQuery<DailyMentionData[]>({
        queryKey: ['mention-share-candidates', fromDate, toDate, includeRadio, weeklyStatus],
        queryFn: async () => {
            return fetchDailyPoliticalMentions({
                from: `${fromDate}T00:00:00.000Z`,
                to: `${toDate}T23:59:59.999Z`,
                includeRadio,
                entityType: 'candidate',
                period: weeklyStatus,
            });
        },
        enabled: isActive && !!fromDate && !!toDate,
        staleTime: 5 * 60 * 1000,
    });

    // Fetch daily/weekly mention data for topics
    const { data: dailyTopicMentions = [], isLoading: isLoadingTopics } = useQuery<DailyMentionData[]>({
        queryKey: ['mention-share-topics', fromDate, toDate, includeRadio, weeklyStatus],
        queryFn: async () => {
            return fetchDailyPoliticalMentions({
                from: `${fromDate}T00:00:00.000Z`,
                to: `${toDate}T23:59:59.999Z`,
                includeRadio,
                entityType: 'topic',
                period: weeklyStatus,
            });
        },
        enabled: isActive && !!fromDate && !!toDate,
        staleTime: 5 * 60 * 1000,
    });

    // Get all candidates from the data
    const allCandidates = useMemo(() => {
        if (!dailyMentions.length) return [];

        const candidateTotals = new Map<string, number>();

        dailyMentions.forEach((day) => {
            Object.keys(day).forEach((key) => {
                if (key !== 'date' && key !== 'fullDate') {
                    const value = typeof day[key] === 'object' ? day[key]?.count || 0 : day[key] || 0;
                    candidateTotals.set(key, (candidateTotals.get(key) || 0) + value);
                }
            });
        });

        return Array.from(candidateTotals.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([name]) => name);
    }, [dailyMentions]);

    // Derive effective selected candidates
    const selectedCandidates = useMemo(() => {
        if (userSelectedCandidates !== null) return userSelectedCandidates;
        return allCandidates.slice(0, 5);
    }, [userSelectedCandidates, allCandidates]);

    // Process chart data with percentages for 100% stacked view
    const chartData = useMemo(() => {
        if (!dailyMentions.length) return [];

        return dailyMentions.map((day) => {
            const dataPoint: any = {
                date: day.date,
                fullDate: day.fullDate || day.date,
            };

            // First, get raw counts for selected candidates
            let totalForPeriod = 0;
            const rawCounts: Record<string, number> = {};

            selectedCandidates.forEach((candidate) => {
                const value = day[candidate];
                const count = typeof value === 'object' ? value?.count || 0 : value || 0;
                rawCounts[candidate] = count;
                totalForPeriod += count;
            });

            // Then calculate percentages
            selectedCandidates.forEach((candidate) => {
                const percentage = totalForPeriod > 0
                    ? Math.round((rawCounts[candidate] / totalForPeriod) * 1000) / 10
                    : 0;
                dataPoint[candidate] = percentage;
                dataPoint[`${candidate}_count`] = rawCounts[candidate];
            });

            dataPoint._total = totalForPeriod;

            return dataPoint;
        });
    }, [dailyMentions, selectedCandidates]);

    // Calculate totals for summary cards
    const candidateTotals = useMemo(() => {
        if (!chartData.length) return [];

        const totals = new Map<string, number>();

        chartData.forEach((day) => {
            selectedCandidates.forEach((candidate) => {
                totals.set(candidate, (totals.get(candidate) || 0) + (day[candidate] || 0));
            });
        });

        const grandTotal = Array.from(totals.values()).reduce((a, b) => a + b, 0);

        return selectedCandidates.map((name, index) => ({
            name,
            count: totals.get(name) || 0,
            percentage: grandTotal > 0 ? Math.round(((totals.get(name) || 0) / grandTotal) * 1000) / 10 : 0,
            color: getColorForCandidate(name, index),
        }));
    }, [chartData, selectedCandidates]);

    // Get all topics from the data
    const allTopics = useMemo(() => {
        if (!dailyTopicMentions.length) return [];

        const topicTotals = new Map<string, number>();

        dailyTopicMentions.forEach((day) => {
            Object.keys(day).forEach((key) => {
                if (key !== 'date' && key !== 'fullDate') {
                    const value = typeof day[key] === 'object' ? day[key]?.count || 0 : day[key] || 0;
                    topicTotals.set(key, (topicTotals.get(key) || 0) + value);
                }
            });
        });

        return Array.from(topicTotals.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([name]) => name);
    }, [dailyTopicMentions]);

    // Derive effective selected topics
    const selectedTopics = useMemo(() => {
        if (userSelectedTopics !== null) return userSelectedTopics;
        return allTopics.slice(0, 8); // Top 8 topics by default
    }, [userSelectedTopics, allTopics]);

    // Process topic chart data with percentages
    const topicChartData = useMemo(() => {
        if (!dailyTopicMentions.length) return [];

        return dailyTopicMentions.map((day) => {
            const dataPoint: any = {
                date: day.date,
                fullDate: day.fullDate || day.date,
            };

            let totalForPeriod = 0;
            const rawCounts: Record<string, number> = {};

            selectedTopics.forEach((topic) => {
                const value = day[topic];
                const count = typeof value === 'object' ? value?.count || 0 : value || 0;
                rawCounts[topic] = count;
                totalForPeriod += count;
            });

            selectedTopics.forEach((topic) => {
                const percentage = totalForPeriod > 0
                    ? Math.round((rawCounts[topic] / totalForPeriod) * 1000) / 10
                    : 0;
                dataPoint[topic] = percentage;
                dataPoint[`${topic}_count`] = rawCounts[topic];
            });

            dataPoint._total = totalForPeriod;

            return dataPoint;
        });
    }, [dailyTopicMentions, selectedTopics]);

    // Calculate topic totals for summary
    const topicTotals = useMemo(() => {
        if (!topicChartData.length) return [];

        const totals = new Map<string, number>();

        topicChartData.forEach((day) => {
            selectedTopics.forEach((topic) => {
                totals.set(topic, (totals.get(topic) || 0) + (day[topic] || 0));
            });
        });

        const grandTotal = Array.from(totals.values()).reduce((a, b) => a + b, 0);

        return selectedTopics.map((name, index) => ({
            name,
            count: totals.get(name) || 0,
            percentage: grandTotal > 0 ? Math.round(((totals.get(name) || 0) / grandTotal) * 1000) / 10 : 0,
            color: getColorForTopic(name, index),
        }));
    }, [topicChartData, selectedTopics]);

    const handleCandidateChange = (event: any) => {
        const value = event.target.value;
        setUserSelectedCandidates(typeof value === 'string' ? value.split(',') : value);
    };

    const handleTopicChange = (event: any) => {
        const value = event.target.value;
        setUserSelectedTopics(typeof value === 'string' ? value.split(',') : value);
    };

    if (!isActive) {
        return null;
    }

    return (
        <Stack spacing={4}>
            {/* Filters */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: 3,
                    border: '1px solid', borderColor: 'divider',
                    bgcolor: 'background.paper',
                }}
            >
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    {/* Date From */}
                    <TextField
                        label={t('period.from')}
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 150 }}
                        size="small"
                    />

                    {/* Date To */}
                    <TextField
                        label={t('period.to')}
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 150 }}
                        size="small"
                    />

                    {/* Period Selector */}
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>{t('period.label')}</InputLabel>
                        <Select
                            value={weeklyStatus}
                            onChange={(e) => setWeeklyStatus(e.target.value as 'daily' | 'weekly')}
                            input={<OutlinedInput label={t('period.label')} />}
                        >
                            <MenuItem value="daily">{t('cadence.daily')}</MenuItem>
                            <MenuItem value="weekly">{t('cadence.weekly')}</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Candidate Selector */}
                    <FormControl size="small" sx={{ minWidth: 250 }}>
                        <InputLabel>{t('fields.candidates')}</InputLabel>
                        <Select
                            multiple
                            value={selectedCandidates}
                            onChange={handleCandidateChange}
                            input={<OutlinedInput label={t('fields.candidates')} />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.slice(0, 3).map((value, idx) => (
                                        <Chip
                                            key={value}
                                            label={value.split(' ').pop()}
                                            size="small"
                                            sx={{
                                                backgroundColor: getColorForCandidate(value, idx),
                                                color: 'white',
                                                fontSize: '0.7rem',
                                            }}
                                        />
                                    ))}
                                    {selected.length > 3 && (
                                        <Chip label={`+${selected.length - 3}`} size="small" />
                                    )}
                                </Box>
                            )}
                        >
                            {allCandidates.map((name, idx) => (
                                <MenuItem key={name} value={name}>
                                    <Checkbox checked={selectedCandidates.includes(name)} />
                                    <Avatar
                                        src={CANDIDATE_IMAGES[name]}
                                        sx={{ width: 24, height: 24, mr: 1 }}
                                    />
                                    <ListItemText primary={name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </Paper>

            {/* Stacked Area Chart */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 3,
                    border: '1px solid', borderColor: 'divider',
                    bgcolor: 'background.paper',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{
                    px: 3,
                    py: 2.5,
                    borderBottom: 1, borderColor: 'divider',
                    bgcolor: 'action.hover',
                }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                            fontSize: '1.1rem',
                            letterSpacing: '-0.01em',
                        }}
                    >
                        {t('charts.evolutionByCandidate')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                        {weeklyStatus === 'weekly' ? t('share.subtitleWeekly') : t('share.subtitleDaily')}
                    </Typography>

                    {/* Candidate summary inline */}
                    {!isLoading && candidateTotals.length > 0 && (
                        <Stack direction="row" spacing={2} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                            {candidateTotals.map((candidate) => (
                                <Box
                                    key={candidate.name}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        px: 1.5,
                                        py: 0.75,
                                        borderRadius: 2,
                                        backgroundColor: theme.palette.action.selected,
                                        border: `1px solid ${candidate.color}40`,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: '50%',
                                            backgroundColor: candidate.color,
                                        }}
                                    />
                                    <Typography variant="body2" fontWeight={600} sx={{ color: theme.palette.text.primary }}>
                                        {candidate.name.split(' ').pop()}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={700} sx={{ color: candidate.color }}>
                                        {candidate.percentage}%
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    )}
                </Box>
                <Box sx={{ p: 3 }}>
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 450 }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Alert severity="error">{t('empty.loadGeneric')}</Alert>
                    ) : chartData.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 450 }}>
                            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                                {t('empty.title')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('empty.selectCandidates')}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ width: '100%', height: 450 }}>
                            <ResponsiveContainer>
                                <AreaChart data={chartData} margin={{ top: 30, right: 50, left: 10, bottom: 10 }} stackOffset="expand">
                                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                                    <XAxis
                                        dataKey="date"
                                        tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                                        tickLine={{ stroke: theme.palette.divider }}
                                        axisLine={{ stroke: theme.palette.divider }}
                                        interval="preserveStartEnd"
                                    />
                                    <YAxis
                                        tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                                        tickLine={{ stroke: theme.palette.divider }}
                                        axisLine={{ stroke: theme.palette.divider }}
                                        tickFormatter={(value) => `${Math.round(value * 100)}%`}
                                        domain={[0, 1]}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary,
                                            border: 'none',
                                            borderRadius: 12,
                                            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                                            padding: '12px 16px',
                                        }}
                                        labelFormatter={(label, payload) => {
                                            if (payload && payload[0]) {
                                                const dateStr = payload[0].payload.fullDate;
                                                const dateObj = new Date(dateStr + 'T12:00:00.000Z');
                                                const total = payload[0].payload._total || 0;
                                                const formattedDate = dateObj.toLocaleDateString('es-CL', {
                                                    timeZone: 'America/Santiago',
                                                    weekday: 'long',
                                                    day: 'numeric',
                                                    month: 'long',
                                                    year: 'numeric'
                                                });
                                                return t('share.tooltipTotal', { date: formattedDate, total: total.toLocaleString() });
                                            }
                                            return label;
                                        }}
                                        formatter={(value, name, props) => {
                                            const count = props.payload[`${name}_count`] || 0;
                                            return [t('share.tooltipSlice', { value, n: count.toLocaleString() }), name];
                                        }}
                                        itemSorter={(item) => -(item.value as number)}
                                    />
                                    <Legend
                                        wrapperStyle={{ paddingTop: '20px' }}
                                        formatter={(value) => (
                                            <span style={{ color: theme.palette.text.primary, fontSize: '12px' }}>{value}</span>
                                        )}
                                    />
                                    {/* Election milestone reference lines */}
                                    {ELECTION_MILESTONES.map((milestone, index) => {
                                        const label = findMilestoneLabel(chartData, milestone.date);
                                        if (!label) return null;
                                        return (
                                            <ReferenceLine
                                                key={index}
                                                x={label}
                                                stroke={milestone.color}
                                                strokeWidth={2}
                                                strokeDasharray="4 4"
                                                label={
                                                    <ReferenceLineLabel
                                                        value={t(`milestones.${milestone.id}.short`)}
                                                        fill={milestone.color}
                                                        fullLabel={t(`milestones.${milestone.id}.full`)}
                                                    />
                                                }
                                            />
                                        );
                                    })}
                                    {selectedCandidates.map((candidateName, idx) => (
                                        <Area
                                            key={candidateName}
                                            type="monotone"
                                            dataKey={candidateName}
                                            stackId="1"
                                            stroke={getColorForCandidate(candidateName, idx)}
                                            fill={getColorForCandidate(candidateName, idx)}
                                            fillOpacity={0.85}
                                        />
                                    ))}
                                </AreaChart>
                            </ResponsiveContainer>
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Topics Stacked Area Chart */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 3,
                    border: '1px solid', borderColor: 'divider',
                    bgcolor: 'background.paper',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{
                    px: 3,
                    py: 2.5,
                    borderBottom: 1, borderColor: 'divider',
                    bgcolor: 'action.hover',
                }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                            fontSize: '1.1rem',
                            letterSpacing: '-0.01em',
                        }}
                    >
                        {t('charts.evolutionByTopic')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                        {weeklyStatus === 'weekly' ? t('share.subtitleWeekly') : t('share.subtitleDaily')}
                    </Typography>

                    {/* Topic selector and summary inline */}
                    <Stack direction="row" spacing={2} sx={{ mt: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>{t('fields.topics')}</InputLabel>
                            <Select
                                multiple
                                value={selectedTopics}
                                onChange={handleTopicChange}
                                input={<OutlinedInput label={t('fields.topics')} />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.slice(0, 2).map((value, idx) => (
                                            <Chip
                                                key={value}
                                                label={topicLabel(value)}
                                                size="small"
                                                sx={{
                                                    backgroundColor: getColorForTopic(value, idx),
                                                    color: 'white',
                                                    fontSize: '0.7rem',
                                                }}
                                            />
                                        ))}
                                        {selected.length > 2 && (
                                            <Chip label={`+${selected.length - 2}`} size="small" />
                                        )}
                                    </Box>
                                )}
                            >
                                {allTopics.map((name, idx) => (
                                    <MenuItem key={name} value={name}>
                                        <Checkbox checked={selectedTopics.includes(name)} />
                                        <ListItemText primary={topicLabel(name)} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Topic summary chips */}
                        {!isLoadingTopics && topicTotals.length > 0 && (
                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                {topicTotals.map((topic) => (
                                    <Box
                                        key={topic.name}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: 2,
                                            backgroundColor: theme.palette.action.selected,
                                            border: `1px solid ${topic.color}40`,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: '50%',
                                                backgroundColor: topic.color,
                                            }}
                                        />
                                        <Typography variant="caption" fontWeight={600} sx={{ color: theme.palette.text.primary }}>
                                            {topicLabel(topic.name)}
                                        </Typography>
                                        <Typography variant="caption" fontWeight={700} sx={{ color: topic.color }}>
                                            {topic.percentage}%
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Stack>
                </Box>
                <Box sx={{ p: 3 }}>
                    {isLoadingTopics ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                            <CircularProgress />
                        </Box>
                    ) : topicChartData.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                                {t('empty.title')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('empty.selectTopics')}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ width: '100%', height: 400 }}>
                            <ResponsiveContainer>
                                <AreaChart data={topicChartData} margin={{ top: 30, right: 50, left: 10, bottom: 10 }} stackOffset="expand">
                                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                                    <XAxis
                                        dataKey="date"
                                        tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                                        tickLine={{ stroke: theme.palette.divider }}
                                        axisLine={{ stroke: theme.palette.divider }}
                                        interval="preserveStartEnd"
                                    />
                                    <YAxis
                                        tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                                        tickLine={{ stroke: theme.palette.divider }}
                                        axisLine={{ stroke: theme.palette.divider }}
                                        tickFormatter={(value) => `${Math.round(value * 100)}%`}
                                        domain={[0, 1]}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary,
                                            border: 'none',
                                            borderRadius: 12,
                                            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                                            padding: '12px 16px',
                                        }}
                                        labelFormatter={(label, payload) => {
                                            if (payload && payload[0]) {
                                                const dateStr = payload[0].payload.fullDate;
                                                const dateObj = new Date(dateStr + 'T12:00:00.000Z');
                                                const total = payload[0].payload._total || 0;
                                                const formattedDate = dateObj.toLocaleDateString('es-CL', {
                                                    timeZone: 'America/Santiago',
                                                    weekday: 'long',
                                                    day: 'numeric',
                                                    month: 'long',
                                                    year: 'numeric'
                                                });
                                                return t('share.tooltipTotal', { date: formattedDate, total: total.toLocaleString() });
                                            }
                                            return label;
                                        }}
                                        formatter={(value, name, props) => {
                                            const count = props.payload[`${name}_count`] || 0;
                                            return [t('share.tooltipSlice', { value, n: count.toLocaleString() }), topicLabel(name as string)];
                                        }}
                                        itemSorter={(item) => -(item.value as number)}
                                    />
                                    <Legend
                                        wrapperStyle={{ paddingTop: '20px' }}
                                        formatter={(value) => (
                                            <span style={{ color: theme.palette.text.primary, fontSize: '12px' }}>{topicLabel(value)}</span>
                                        )}
                                    />
                                    {/* Election milestone reference lines */}
                                    {ELECTION_MILESTONES.map((milestone, index) => {
                                        const label = findMilestoneLabel(topicChartData, milestone.date);
                                        if (!label) return null;
                                        return (
                                            <ReferenceLine
                                                key={index}
                                                x={label}
                                                stroke={milestone.color}
                                                strokeWidth={2}
                                                strokeDasharray="4 4"
                                                label={
                                                    <ReferenceLineLabel
                                                        value={t(`milestones.${milestone.id}.short`)}
                                                        fill={milestone.color}
                                                        fullLabel={t(`milestones.${milestone.id}.full`)}
                                                    />
                                                }
                                            />
                                        );
                                    })}
                                    {selectedTopics.map((topicName, idx) => (
                                        <Area
                                            key={topicName}
                                            type="monotone"
                                            dataKey={topicName}
                                            stackId="1"
                                            stroke={getColorForTopic(topicName, idx)}
                                            fill={getColorForTopic(topicName, idx)}
                                            fillOpacity={0.85}
                                        />
                                    ))}
                                </AreaChart>
                            </ResponsiveContainer>
                        </Box>
                    )}
                </Box>
            </Paper>
        </Stack>
    );
}
